import { Grid2 } from '@mui/material';
import { MqttClient } from 'mqtt';
import { DataPoint, DataPointWithValue } from '@src/types/DataPoint';
import { useCallback, useMemo } from 'react';
import SimplePointDataTable from '@components/_shared/SimplePointDataTable';
import useSubscription from '@hooks/useSubscription';
import {
  calculateDataPointValue,
  createDataPointsMap,
} from '@utils/dataPoints';

interface Props {
  dataPointsMatrix: DataPoint[][];
  useNewClient?: MqttClient;
}

function MultiColPointDataTable({ dataPointsMatrix, useNewClient }: Props) {
  // biome-ignore lint/correctness/useExhaustiveDependencies: Keep useNewClient as a dependency to avoid missing data
  const allDataPoints = useMemo(
    () => dataPointsMatrix.flat(),
    [dataPointsMatrix, useNewClient],
  );

  const allDataPointsWithValues = useSubscription(allDataPoints, useNewClient);

  // Memoize the function to find a data point by alias
  const findDataPointByAlias = useCallback(
    (pointAlias: string) =>
      allDataPointsWithValues.find((point) => point.pointAlias === pointAlias),
    [allDataPointsWithValues],
  );

  const dataPointsMatrixWithValues = useMemo(() => {
    // Create a map for quick lookup
    const dataPointsMap = createDataPointsMap(allDataPointsWithValues);

    // Process the data points matrix
    return dataPointsMatrix.map((columnDataPoints) =>
      columnDataPoints.map<DataPointWithValue>((dataPoint) => {
        // Get existing data point with values from subscription, if available
        const existingDataPoint = dataPointsMap.get(dataPoint.fullTopic);

        // If it has no valueGenerationFunction, just return the existing point or a default
        if (!dataPoint.valueGenerationFunction) {
          return (
            existingDataPoint || {
              ...dataPoint,
              value: undefined,
              ts: undefined,
            }
          );
        }

        const calculationResult = calculateDataPointValue(
          dataPoint,
          findDataPointByAlias,
        );

        // If calculation successful, return data point with calculated value
        if (calculationResult) {
          return {
            ...dataPoint,
            value: calculationResult.value,
            ts: calculationResult.timestamp,
          };
        }

        // Otherwise return existing or default
        return (
          existingDataPoint || {
            ...dataPoint,
            value: undefined,
            ts: undefined,
          }
        );
      }),
    );
  }, [dataPointsMatrix, allDataPointsWithValues, findDataPointByAlias]);

  // Calculate column width once
  const columnWidth = useMemo(
    () => Math.floor(12 / dataPointsMatrix.length),
    [dataPointsMatrix.length],
  );

  return (
    <>
      {dataPointsMatrixWithValues.map((columnSectionDataPoints) => {
        const columnId = columnSectionDataPoints
          .map((dataPoint) => dataPoint.pointAlias)
          .join('-');

        return (
          <Grid2 size={{ xs: columnWidth }} key={columnId}>
            <SimplePointDataTable data={columnSectionDataPoints} />
          </Grid2>
        );
      })}
    </>
  );
}

export default MultiColPointDataTable;
